import { CoreError, assertSafeInt } from './errors'
import { computeLine, lineGross, type LineAmounts, type LineInput } from './lines'
import { allocateProportionally, type Paisa } from './money'
import { divRound, toSafeNumber } from './rounding'

export type RoundOffMode = 'none' | 'nearest_rupee'

export interface DocumentTotals {
  subtotalPaisa: Paisa
  discountPaisa: Paisa
  taxablePaisa: Paisa
  taxPaisa: Paisa
  roundOffPaisa: Paisa
  totalPaisa: Paisa
}

export interface ComputedDocument {
  lines: LineAmounts[]
  totals: DocumentTotals
}

/**
 * Spreads a document-level discount over the lines, proportional to each
 * line's amount after its own discount, so the header always equals the sum
 * of the lines (the server rejects anything else).
 */
export function applyDocumentDiscount(
  lines: readonly LineInput[],
  documentDiscountPaisa: Paisa,
): LineInput[] {
  assertSafeInt(documentDiscountPaisa, 'documentDiscountPaisa')
  if (documentDiscountPaisa === 0) return lines.map((l) => ({ ...l }))
  const nets = lines.map((l) => lineGross(l.qtyMilli, l.unitPricePaisa) - (l.discountPaisa ?? 0))
  const totalNet = nets.reduce((a, n) => a + n, 0)
  if (documentDiscountPaisa < 0 || documentDiscountPaisa > totalNet) {
    throw new CoreError(
      'invalid_discount',
      'discount must be between 0 and the amount before discount',
    )
  }
  const shares = allocateProportionally(documentDiscountPaisa, nets)
  return lines.map((l, i) => ({ ...l, discountPaisa: (l.discountPaisa ?? 0) + (shares[i] ?? 0) }))
}

/** Round-off that brings the total to the nearest rupee (|round-off| ≤ 50 paisa). */
export function roundOffFor(amountPaisa: Paisa, mode: RoundOffMode): Paisa {
  if (mode === 'none') return 0
  const rounded = toSafeNumber(divRound(BigInt(amountPaisa), 100n) * 100n)
  return rounded - amountPaisa
}

export function computeDocument(
  lines: readonly LineInput[],
  options: { documentDiscountPaisa?: Paisa; roundOff?: RoundOffMode } = {},
): ComputedDocument {
  const withDiscount = applyDocumentDiscount(lines, options.documentDiscountPaisa ?? 0)
  const amounts = withDiscount.map(computeLine)
  const sum = (pick: (l: LineAmounts) => number) => amounts.reduce((a, l) => a + pick(l), 0)
  const taxablePaisa = sum((l) => l.taxablePaisa)
  const taxPaisa = sum((l) => l.taxPaisa)
  const roundOffPaisa = roundOffFor(taxablePaisa + taxPaisa, options.roundOff ?? 'none')
  return {
    lines: amounts,
    totals: {
      subtotalPaisa: sum((l) => l.grossPaisa),
      discountPaisa: sum((l) => l.discountPaisa),
      taxablePaisa,
      taxPaisa,
      roundOffPaisa,
      totalPaisa: taxablePaisa + taxPaisa + roundOffPaisa,
    },
  }
}
